import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import ProductSearchSelect from '@/components/ProductSearchSelect';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import PhotoCard from '@/design_system/PhotoCard';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconEdit from '@/icons/Edit.svg';
import { getProductPictureFromArticleColor } from '@/models/article';
import { useUpdateArticleExchange } from '@/models/articleExchange';
import { useColorOptions } from '@/models/product';
import { useSizesOptionsSorted } from '@/models/sizeProduct';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { Color } from '@/routes/Requests/Request/Article/Color';
import Size from '@/routes/Requests/Request/Article/Size';
import { useDatabaseTranslation } from '@/services/i18n';
import useViewPort from '@/utils/useViewport';

import { EditExchangeMobile, ServiceExchangePrice } from './ServiceExchangePrice';

export const ReplacementProductInput = () => {
  const { t } = useLingui();
  const { article, errors } = useArticleContext();
  const { mutate: updateArticleExchange } = useUpdateArticleExchange(article.id);

  return (
    <ProductSearchSelect
      article={article}
      isReplacementProduct
      label={t({
        id: 'service-choice.exchange.replacement-product.label',
        message: 'Replacement product',
      })}
      placeholder={t({
        id: 'service-choice.exchange.replacement-product.search.placeholder',
        message: 'Search by product name or reference',
      })}
      value={article.snapshot.articleExchange?.replacementProduct}
      onChange={(product) => {
        updateArticleExchange({ replacementProductId: product?.id || null });
      }}
      error={
        errors.services?.exchange?.missingReplacementProduct
          ? t({
              id: 'replacement-product.errors.missing',
              message: 'Please select a replacement product',
            })
          : undefined
      }
    />
  );
};

export const ReplacementProduct = () => {
  const { article, view } = useArticleContext();
  const colorOptions = useColorOptions({ article, isReplacementProduct: true });
  const { sizesOptions } = useSizesOptionsSorted({ article, isReplacementProduct: true });

  if (!article.snapshot.articleExchange?.replacementProduct) {
    return null;
  }

  const showColorInput =
    view.services.exchange.replacementProduct.editable &&
    view.details.color.shown &&
    !!colorOptions.length;

  const showSizeInput =
    view.services.exchange.replacementProduct.editable &&
    view.details.size.shown &&
    !!sizesOptions.length;

  return (
    <Box padding="0">
      {!view.services.exchange.replacementProduct.editable && (
        <>
          {' '}
          <div style={{ padding: '0.75rem' }}>
            <Paragraph size="200">
              <Trans id={'service-choice.exchange.replacement-item'}>Replacement item</Trans>
            </Paragraph>
          </div>
          <hr />
        </>
      )}

      <div style={{ padding: '0.75rem' }}>
        <ReplacementProductInfo />
      </div>

      {(showColorInput || showSizeInput) && (
        <>
          <hr />
          <Stack row mobileColumn gap="1rem" padding="0.75rem">
            {showColorInput && <Color isReplacementProduct />}
            {showSizeInput && <Size isReplacementProduct />}
          </Stack>
        </>
      )}

      <ServiceExchangePrice />
    </Box>
  );
};

const ReplacementProductInfo = () => {
  const { _db } = useDatabaseTranslation();
  const { isMobile } = useViewPort();
  const { article, view } = useArticleContext();
  const [isEditDialogOpen, setEditDialogOpen] = useState(false);

  const replacementProduct = article.snapshot.articleExchange?.replacementProduct;

  if (!replacementProduct) {
    return null;
  }

  const replacementProductColor = article.snapshot.articleExchange?.replacementColor
    ? _db(article.snapshot.articleExchange.replacementColor)
    : undefined;

  const replacementProductSize = article.snapshot.articleExchange?.replacementSize
    ? _db(article.snapshot.articleExchange.replacementSize)
    : undefined;

  const replacementProductPicture = getProductPictureFromArticleColor({
    articleExchange: article.snapshot.articleExchange,
  });

  const showEditButton =
    isMobile &&
    (view.services.exchange.workshopPrice.editable ||
      view.services.exchange.organizationPrice.editable);

  const productPicture = getProductPictureFromArticleColor({
    articleExchange: article.snapshot.articleExchange,
  });

  return (
    <Stack row gap="12px" alignItems="center" justifyContent="space-between">
      <Stack row gap="12px" alignItems="center">
        {!!replacementProductPicture && (
          <PhotoCard
            name={replacementProduct.name}
            alt={replacementProduct.name}
            url={productPicture ?? undefined}
          />
        )}

        <Stack alignItems="flex-start">
          <InlineText weight="medium">{replacementProduct.name}</InlineText>

          <InlineText size="200" color="secondary">
            <Trans id="article.reference.short">Ref:</Trans> {replacementProduct.externalId}
          </InlineText>

          <Stack row gap="4px">
            {!!replacementProductColor && (
              <InlineText size="200" color="secondary">
                <Trans id="product.color.label">Color:</Trans> {replacementProductColor}
              </InlineText>
            )}
            {!!replacementProductColor && !!replacementProductSize && (
              <InlineText size="200" color="secondary">
                •
              </InlineText>
            )}
            {!!replacementProductSize && (
              <InlineText size="200" color="secondary">
                <Trans id="product.size.label">Size:</Trans> {replacementProductSize}
              </InlineText>
            )}
          </Stack>
        </Stack>
      </Stack>

      {showEditButton && <EditButton onPressEdit={() => setEditDialogOpen(true)} />}

      <EditExchangeMobile isOpen={isEditDialogOpen} setIsOpen={setEditDialogOpen} />
    </Stack>
  );
};

const EditButton = ({ onPressEdit }: { onPressEdit: () => void }) => {
  const { t } = useLingui();

  return (
    <Button
      variant="secondary"
      size="medium"
      ariaLabel={t({ id: 'article.form.actions.price.edit', message: 'Edit' })}
      tooltip={t({ id: 'article.form.actions.price.edit', message: 'Edit' })}
      iconOnly
      onPress={onPressEdit}
    >
      <IconEdit />
    </Button>
  );
};
